/**
 * The frozen operation surface — T024.
 *
 * What this module is
 * -------------------
 * `contracts/operations.md` (reconciled at TA10) defines the operations; `ARCHITECTURE.md` § D-1
 * packages them behind three callables and three background functions that dispatch on an
 * `{ op, params }` envelope. This module is the single place both the SPA and the Cloud Functions
 * read that surface from:
 *
 *  - `OPERATION_NAMES` — every dispatchable operation name, derived one-for-one from the contract;
 *  - `OPERATIONS` — a per-operation registry giving its category, the function that serves it, and
 *    its request/response Zod schemas;
 *  - `operationEnvelopeSchema` — a real discriminated union on `op`, so an unknown operation name is
 *    rejected by the schema itself rather than by a handler lookup.
 *
 * Every request/response schema of the 68 non-smoke operations is explicit and marked `specified`.
 * The separate `operationSchemas.ts` module keeps those shapes reviewable while this registry owns
 * names, packaging and the discriminated envelope. `assertSchemasFrozen()` is the executable gate:
 * any future placeholder fails closed. The three walking-skeleton operations remain specified here
 * because their contracts were introduced by T006 rather than `contracts/operations.md`.
 *
 * Places where `contracts/operations.md` is not the last word:
 *  - **File upload — resolved here, not deferred to T024.** That document's single
 *    `admin.materials.uploadFile` row is an obsolete shorthand for a two-step flow. The accepted
 *    architecture (`ARCHITECTURE.md` § 10, Upload) names `admin.materials.beginUpload`, and accepted
 *    `tasks.md` T060 implements `admin.materials.beginUpload` and `finishUpload`. The runbook's
 *    authority order (§ 3: architecture → `tasks.md` → `contracts/`) makes those two the real
 *    operation names, so both are registered here and the superseded `admin.materials.uploadFile` is
 *    **not** a valid operation name.
 *  - `admin.answers.confirm` is marked "(batch, with preview)" but, unlike
 *    `admin.entitlements.extendPreview`, no preview operation is named for it in any accepted
 *    document. It stays one operation.
 *  - **The administrator curriculum read side — added at T091, for the same reason.** That document's
 *    § Curriculum table names only writes, so an administrator could create a course but never reload
 *    one without a student-scoped call (`docs/evidence/wp3/t053-design.md` § Frozen-source conformance
 *    errata (3)). Accepted `tasks.md` T091 names `admin.courses.list`, `admin.courses.get` and
 *    `admin.groups.list`, and the runbook's authority order puts `tasks.md` above `contracts/`.
 *    `BOUNDARIES.md` § 3 is explicit that *adding* an operation is not a freeze violation — changing
 *    one is — and no existing wire shape is touched by their addition.
 *  - **The selection-scoped AI edit — added at T095, for the same reason.** That document predates the
 *    owner's 2026-09-13 decision that an administrator may have one selected passage of an answer
 *    rewritten, so it names no operation for it. Accepted `tasks.md` T095 names the durable job core:
 *    three administrator operations to create, retry and replace one job, and one system operation for
 *    the worker that processes it. All four are *additions*: no existing operation's shape changes, no
 *    student gains a capability, and the four public Question statuses are untouched.
 *  - **The administrator answer restore — added at T094, for the same reason.** That document's
 *    § Q&A table names the five answer writes but no way back to an earlier stored version, so an
 *    administrator whose regeneration produced a worse answer could only retype the old one — and the
 *    history the panel already shows would be decoration. Accepted `tasks.md` T094 names an
 *    administrator restore that "appends a new answer version rather than mutating history", and the
 *    runbook's authority order puts `tasks.md` above `contracts/`. It is an *addition*: no existing
 *    operation's shape changes, no student gains a capability, and history stays append-only.
 *  - **The administrator preview download — added at T062, for the same reason.** `materials.fileAccess`
 *    is deliberately enrolment-gated (T060 D7), so an administrator — who holds no Enrollment — cannot
 *    obtain the protected bytes of a material they are authoring. Approved `docs/evidence/wp3/t062-design.md`
 *    D1 therefore names one admin-only `admin.materials.fileAccess`, so that R2-02's single shared PDF
 *    reader has an administrator source. It is an *addition*: no existing operation's shape changes,
 *    and no student gains a new capability.
 *  - **The completion mark — added at PRD-07, for the same reason.** Production has no per-material
 *    completion concept anywhere: `reader.progress.save` stores one auto-resume position and nothing
 *    counts or marks a material done, so the owner-approved `დავასრულე` control has nothing to call.
 *    `docs/design/product-redesign-addendum.md` § 3 freezes one student operation for it,
 *    `materials.completion.set`, and freezes the progress a course card shows as *additive fields* on
 *    the existing `courses.listMine` / `courses.get` reads rather than as a second operation. It is an
 *    *addition*: no existing operation's shape changes, no administrator gains a surface, and every
 *    payload written against the pre-PRD-07 course shape still parses.
 *
 * `BOUNDARIES.md` § 2/§ 3: `zod` is the only runtime dependency, and this workspace is
 * integrator-owned — feature streams propose changes, the integrator merges them.
 *
 * Every exported list here is `Object.freeze`d as well as `as const`. `isOperationName` and
 * `operationsServedBy` read these arrays at call time, while `operationNameSchema`,
 * `operationEnvelopeSchema` and `OPERATIONS` are built from them once at module load: a `push` onto
 * an unfrozen export would make the guards accept an operation the envelope and the registry do not
 * know, which is precisely the "permissive unknown operation name" this skeleton must not allow.
 */
import { z } from 'zod';
import { type DeployedFunctionName } from './types';
import { OPERATION_SCHEMAS } from './operationSchemas';

/* ------------------------------------------------------------------ *
 * Categories and the dispatch surface
 * ------------------------------------------------------------------ */

/** The four authorization categories of `contracts/operations.md` (its § headings). */
export const OPERATION_CATEGORIES = Object.freeze(['bootstrap', 'student', 'admin', 'system'] as const);

export type OperationCategory = (typeof OPERATION_CATEGORIES)[number];

/**
 * `fileGateway` is deployed (`types.ts` `DEPLOYED_FUNCTIONS`) but is an `onRequest` authenticated
 * streaming download — the documented fallback when signed URLs are unavailable. `uploadSweep` is the
 * hourly FR-013 upload sweep, whose only caller is Cloud Scheduler and which answers nothing. Neither
 * ever receives an `{ op, params }` envelope, so no operation may name either.
 */
export const NON_DISPATCHING_FUNCTIONS = Object.freeze(['fileGateway', 'uploadSweep'] as const);

export type DispatchingFunctionName = Exclude<
  DeployedFunctionName,
  (typeof NON_DISPATCHING_FUNCTIONS)[number]
>;

/* ------------------------------------------------------------------ *
 * The operation names, grouped exactly as the contract groups them
 * ------------------------------------------------------------------ */

/**
 * `contracts/operations.md` § Bootstrap — served by `bootstrapApi`.
 *
 * *(Owner, 2026-10-12)* `client.report` lives here because this is the one dispatcher that serves an
 * Account in any status: a pending or deactivated student's browser fails in the same ways an approved
 * one's does, and the report is only ever written to the log — it grants nothing and reads nothing.
 */
export const BOOTSTRAP_OPERATIONS = Object.freeze(['account.bootstrap', 'me.state', 'client.report'] as const);

/** `contracts/operations.md` § Student (incl. § Student Q&A list and search) — served by `studentApi`. */
export const STUDENT_OPERATIONS = Object.freeze([
  'me.get',
  'courses.listMine',
  'courses.get',
  'materials.fileAccess',
  // *(2026-09-26)* A reading body kept in Storage, fetched when the reader opens that one material.
  'materials.body',
  'questions.submit',
  'questions.deleteMine',
  'questions.list',
  'questions.search',
  'questions.get',
  'favorites.toggle',
  'favorites.list',
  /*
   * *(002-course-content-reader)* The student read of protected content assets.
   *
   * One call for up to fifty asset ids of one visible document, rather than one callable per image:
   * a reading material with twelve inline images would otherwise open with twelve authorization
   * round trips, each re-deriving the same entitlement snapshot. The response carries short-lived
   * URLs and never a storage path (FR-012).
   */
  'contentAssets.fileAccess',
  /*
   * *(002-course-content-reader)* The seven private reader operations.
   *
   * They are **student** operations on `studentApi`, and that is the whole of FR-028's administrator
   * denial: highlights, bookmarks and reading progress belong to the account that made them, so no
   * administrator token may reach them at all. Putting one of these on `adminApi` would not be a
   * handler bug to be caught by a negative test — it would be a contract that permits the read.
   *
   * `reader.open` mints the session epoch every later write is compared against; `reader.progress.save`
   * answers `{applied:false}` for an older epoch or a non-increasing sequence rather than moving a
   * newer device's position backward (R4, FR-031). Every request carries the course/section/material
   * coordinates so authority is re-established on the server for each one.
   */
  'reader.open',
  'reader.progress.save',
  'reader.highlights.create',
  'reader.highlights.update',
  'reader.highlights.delete',
  // *(Re-audit N02, OD-SR2)* The atomic overlap replacement: a student operation like the seven.
  'reader.highlights.replace',
  'reader.bookmarks.create',
  'reader.bookmarks.delete',
  /*
   * *(PRD-07, `docs/design/product-redesign-addendum.md` § 3 FREEZE-1)* The one completion operation.
   *
   * **`materials.*` and not `reader.*`**, because completion applies to all four material types — a
   * video embed, an external link and a protected PDF are finished exactly as a reading material is,
   * and none of them opens a reader session. Naming it `reader.completion.set` would have promised a
   * paginated document the other three do not have.
   *
   * It is a **student** operation on `studentApi` for the same reason the seven private reader
   * operations are: the record is per-account private study state, so "no administrator can read it"
   * has to be a property of the dispatch surface rather than of a handler's good intentions. No
   * administrator counterpart exists, and `tests/boundaries/content-reader.test.ts` refuses one by
   * name rather than by allowlist.
   *
   * The request states the **desired final state**; nothing else in the product sets completion.
   * Setting the state that is already stored performs no write and answers `applied:false` with the
   * stored values — the same convergence style `reader.progress.save` uses.
   */
  'materials.completion.set',
] as const);

/** `contracts/operations.md` § Admin, in its five sub-sections — all served by `adminApi`. */
export const ADMIN_OPERATIONS = Object.freeze([
  // § Access
  'admin.requests.list',
  // *(AR-11 correction)* The single-request read the routed workspace needs; see `operationSchemas.ts`.
  'admin.requests.get',
  'admin.requests.decide',
  'admin.preInvites.create',
  'admin.preInvites.list',
  'admin.preInvites.update',
  'admin.preInvites.delete',
  'admin.students.list',
  'admin.students.get',
  'admin.students.update',
  'admin.students.setStatus',
  'admin.students.recordDeletionRequest',
  /*
   * *(OD-2026-09-24, OD-S1/S3/S4/S6/S13)* The Admin Students register and one student's course
   * progress. Two reads, both additions: the register merges approved and suspended accounts with the
   * open pre-invitations the design shows beside them, and the progress read gives the administrator
   * the per-course „7 / 12“ figure the student already sees — counts only, never a mark, a date or a
   * reading position (FR-028 as amended by OD-S6).
   */
  'admin.students.directory',
  'admin.students.progress',
  // § Curriculum
  // *(T091)* The three read operations come first, exactly as § Access lists `admin.requests.list`
  // before `decide` and `admin.students.list`/`get` before `update`. Their derivation is the third
  // exception recorded in the module comment above.
  // *(AR-12 correction)* The bounded id -> name projection every administrator surface reads so that
  // no stored identifier is ever shown to a person. It is a read of two directories the same
  // administrator may already list.
  'admin.curriculum.names',
  // *(ledger 59)* The bounded whole-scope totals both overviews state — question totals, group count
  // and lecture progress. An *addition* for the same reason `admin.curriculum.names` is one: without
  // it an overview can only sum the page it loaded and call that the total.
  'admin.curriculum.overview',
  'admin.courses.list',
  'admin.courses.get',
  'admin.courses.create',
  'admin.courses.update',
  'admin.courses.archive',
  // *(OD-2026-09-23)* draft → active.
  'admin.courses.activate',
  /*
   * *(OD-7)* The course lifecycle amendment of `docs/design/admin-redesign-addendum.md` § 8.6.
   *
   * Four additions beside the archive they complete: the explicit way back out of the archive, the
   * read-only impact preview, the reversible thirty-day deletion and the restore. `BOUNDARIES.md`
   * § 3 permits adding an operation; none of the shapes above them changes, and the owner amendment
   * supersedes the earlier MVP sentence that exposed no administrator deletion at all.
   */
  'admin.courses.unarchive',
  'admin.courses.deletePreview',
  'admin.courses.delete',
  'admin.courses.restore',
  // *(OD-2026-09-24, OD-C12)* The permanent delete of a course already in the Trash — the retention
  // run's own aggregate purge, run now instead of thirty days on.
  'admin.courses.purge',
  // *(AR-09)* The explicit, previewed propagation of a course template to its not-started groups.
  // The pair sits beside the course write it belongs to, and follows the
  // `admin.entitlements.extendPreview` / `.extend` precedent: one operation that writes nothing and
  // one that does, over one request shape.
  'admin.courses.applyTemplatePreview',
  'admin.courses.applyTemplate',
  'admin.groups.list',
  'admin.groups.create',
  'admin.groups.update',
  'admin.groups.archive',
  // *(OD-7)* The group equivalents, in the same order.
  'admin.groups.unarchive',
  'admin.groups.deletePreview',
  'admin.groups.delete',
  'admin.groups.restore',
  'admin.sections.create',
  'admin.sections.update',
  'admin.sections.reorder',
  'admin.sections.setPublished',
  // *(OD-2026-10-12)* The thirty-day section Trash, beside the four section writes it completes.
  'admin.sections.deletePreview',
  'admin.sections.delete',
  'admin.sections.restore',
  'admin.sections.listDeleted',
  'admin.materials.create',
  'admin.materials.update',
  'admin.materials.reorder',
  'admin.materials.setPublished',
  /*
   * *(OD-2026-09-24, OD-G6)* The thirty-day material Trash: the soft delete, its restore and the
   * bounded read of what one course's Trash holds. Additions beside the four writes they complete;
   * the daily retention run removes a deleted material permanently thirty days on.
   */
  'admin.materials.delete',
  'admin.materials.restore',
  'admin.materials.listDeleted',
  // *(OD-2026-10-12)* A material into another section of its course.
  'admin.materials.move',
  // *(2026-09-26)* A reading body kept in Storage, fetched when the editor opens that one material.
  'admin.materials.body',
  // Two operations, per ARCHITECTURE.md § 10 (Upload) and tasks.md T060 — they supersede the
  // obsolete single `admin.materials.uploadFile` row of contracts/operations.md.
  'admin.materials.beginUpload',
  'admin.materials.finishUpload',
  'admin.materials.deleteFile',
  // *(T062)* The administrator preview download, appended after the accepted T060 block rather than
  // placed before it: those three keep the order they were accepted in, and the fourth exception in
  // the module comment above records where this name comes from.
  'admin.materials.fileAccess',
  /*
   * *(002-course-content-reader)* The protected content-asset handshake — course covers and inline
   * material/explanation images.
   *
   * It **generalizes** the accepted T060 PDF flow rather than replacing it: `admin.materials.*` keeps
   * every shape and every behaviour it has. What is new is that an asset is created before it has a
   * parent — an administrator inserts an image while composing a document that is not saved yet — so
   * a content asset is its own record with its own `pending → attached → deleted` lifecycle, and the
   * save that references it is what attaches it. `discard` is the explicit way back out for a
   * candidate its creator abandoned before saving; the 24-hour sweep is the backstop, not the path.
   *
   * `admin.contentAssets.fileAccess` exists for the same reason `admin.materials.fileAccess` does
   * (T062 D1): an administrator holds no Enrollment, so the student batch read cannot serve the
   * authoring surface, and a draft material's images have to be visible while it is still a draft.
   */
  'admin.contentAssets.beginUpload',
  'admin.contentAssets.finishUpload',
  'admin.contentAssets.discard',
  'admin.contentAssets.fileAccess',
  /*
   * *(002-course-content-reader)* The five material-explanation operations.
   *
   * An explanation is administrator-authored rich content bound to one contiguous passage of a
   * reading material, and the five names are the five genuinely different acts: read them all for one
   * material, attach one to a passage, edit the content without touching the passage, remove one, and
   * — separately and explicitly — move one to a different passage. `reanchor` is its own operation
   * precisely because it is the only anchor-changing act: folding it into `update` would make every
   * ordinary content edit capable of silently relocating an explanation (FR-019).
   */
  'admin.materialExplanations.list',
  'admin.materialExplanations.create',
  'admin.materialExplanations.update',
  'admin.materialExplanations.delete',
  'admin.materialExplanations.reanchor',
  // § Enrollment and entitlement (incl. § Entitlement extension, which names both variants)
  'admin.enrollments.create',
  'admin.enrollments.transfer',
  'admin.enrollments.close',
  'admin.entitlements.extendPreview',
  'admin.entitlements.extend',
  // § Q&A workspace
  'admin.questions.list',
  'admin.questions.get',
  'admin.questions.editText',
  'admin.questions.setVisibilityScope',
  'admin.answers.saveDraft',
  'admin.answers.regenerate',
  'admin.answers.overrideStale',
  'admin.answers.confirm',
  'admin.answers.editPublished',
  'admin.answers.unpublish',
  // *(T094)* The administrator restore of a stored answer version — an addition, for the reason the
  // module comment's fifth bullet records.
  'admin.answers.restoreVersion',
  // *(T095)* The three administrator operations of the durable selection-scoped AI edit. They are
  // additions for the reason the module comment's sixth bullet records, and they touch no existing
  // wire shape: a selection job is its own record under its own question.
  'admin.selectionEdits.create',
  'admin.selectionEdits.retry',
  'admin.selectionEdits.replace',
  'admin.ai.prepareNow',
  'admin.ai.prepareAll',
  'admin.questions.delete',
  // *(OD-2026-09-23)* Restore of a soft-deleted question (T14) and its permanent delete.
  'admin.questions.restore',
  'admin.questions.purge',
  // § Operations
  'admin.audit.list',
  'admin.batches.get',
  'admin.system.status',
] as const);

/** `contracts/operations.md` § System — IAM/OIDC identity, no user token; one background function each. */
export const SYSTEM_OPERATIONS = Object.freeze([
  'system.ai.hourlyRun',
  'system.ai.processQuestion',
  // *(T095)* One selection-edit job, processed by the same deployed worker under the same lease
  // discipline. Its own operation rather than a flag on `processQuestion`: the two read different
  // documents, take different leases and have different outcomes, and one operation that meant two
  // things would be a dispatcher deciding which by inspecting its own parameters.
  'system.ai.processSelectionEdit',
  'system.retention.purgeDaily',
] as const);

/**
 * The three walking-skeleton operations of `tasks.md` T006 — **added here deliberately**, resolving
 * the sequencing gap recorded in `docs/evidence/wp0/foundation.md` § 35.
 *
 * They are not in `contracts/operations.md`; they are named by the accepted task list, which
 * `docs/EXECUTION-RUNBOOK.md` § 3 ranks above `contracts/`. `BOUNDARIES.md` § 3 says *adding* an
 * operation is not a contract-freeze violation (changing one is), and this workspace is
 * integrator-owned, so the addition belongs here rather than in a dispatcher that accepts
 * unregistered names — which is exactly the permissive back door the T005 skeleton refuses.
 *
 * They are `admin.*`, category `admin`, served by `adminApi` only, like every other admin operation:
 * D-12 wants a walking skeleton that proves one Firestore write, one Cloud Task and one signed-URL
 * round trip on real infrastructure (T013), and it is reused by `scripts/smoke.ts` (T080). Nothing
 * student-facing may reach them, which B9 and the Functions registry both enforce.
 *
 * Unlike the 65 contract operations, these three carry **real** schemas and are marked `'specified'`
 * (see the registry below): their shape is decided by this task, so a placeholder would be a
 * pretence rather than an honest deferral.
 */
export const SMOKE_OPERATIONS = Object.freeze([
  'admin.smoke.write',
  'admin.smoke.enqueue',
  'admin.smoke.signedUrl',
] as const);

/**
 * The one caller-supplied field every smoke operation takes: a short slug that ends up in the
 * Firestore document id, the Cloud Task payload and the Storage object path, so a smoke run is
 * identifiable and its blast radius stays inside the dedicated smoke namespace.
 *
 * The lexical rule is the validation — lowercase alphanumerics and `-` only, never a path segment
 * separator, `.` or `..` — so no handler has to sanitize it before composing an identifier.
 */
export const smokeMarkerSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'a smoke marker is 1–40 lowercase alphanumerics or "-", not starting with "-"');

/** The content types the signed-URL round trip may exercise: a text probe, and the real PDF case. */
export const SMOKE_CONTENT_TYPES = Object.freeze(['text/plain', 'application/pdf'] as const);

export const smokeWriteRequestSchema = z.object({ marker: smokeMarkerSchema }).strict();

export const smokeWriteResponseSchema = z
  .object({
    documentPath: z.string().min(1),
    documentId: z.string().min(1),
    writtenAt: z.iso.datetime(),
  })
  .strict();

export const smokeEnqueueRequestSchema = z.object({ marker: smokeMarkerSchema }).strict();

/**
 * The **fully qualified regional** Cloud Tasks target for `aiWorker`, as
 * `locations/{region}/functions/aiWorker`.
 *
 * This is not decoration. `firebase-admin` 13.10.0 resolves an unqualified function name to the
 * default location: `parseResourceName('aiWorker', 'functions')` returns only a `resourceId`, after
 * which `functions-api-client-internal.js` applies `resources.locationId ||= DEFAULT_LOCATION` with
 * `DEFAULT_LOCATION = 'us-central1'`, and that location is then used for **both** the Cloud Tasks
 * queue URL and the task's target function URL. `aiWorker` is deployed in `europe-west1`, so a bare
 * name enqueues to a queue that does not exist for us. The qualified form fixes the region while
 * still letting the SDK fill the project id from the runtime environment.
 *
 * The pattern deliberately does not pin one region: the region a function is deployed to belongs to
 * the Functions workspace, not to the shared contract. What the contract enforces is that the target
 * is *qualified at all*, which is exactly the defect class above.
 */
export const SMOKE_TASK_TARGET_PATTERN = /^locations\/[a-z0-9-]+\/functions\/aiWorker$/;

export const smokeEnqueueResponseSchema = z
  .object({
    /**
     * The **logical** function name — the queue is named after the function that drains it
     * (packaging table, D-1). Stable, and what a reader recognises.
     */
    queue: z.literal('aiWorker'),
    /**
     * The **regional resource name actually handed to the Admin SDK**, reported so a smoke run says
     * which queue it targeted instead of leaving the region implicit. A bare `aiWorker` here fails
     * this schema, so the operation can never report success for an unqualified enqueue.
     */
    queueTarget: z
      .string()
      .regex(SMOKE_TASK_TARGET_PATTERN, 'the Cloud Tasks target must be locations/{region}/functions/aiWorker'),
    taskId: z.string().min(1),
    enqueuedAt: z.iso.datetime(),
  })
  .strict();

export const smokeSignedUrlRequestSchema = z
  .object({
    marker: smokeMarkerSchema,
    contentType: z.enum(SMOKE_CONTENT_TYPES).default('text/plain'),
  })
  .strict();

export const smokeSignedUrlResponseSchema = z
  .object({
    objectPath: z.string().min(1),
    /** Both halves of the round trip are issued together: PUT the upload URL, then GET the download URL. */
    uploadUrl: z.url(),
    downloadUrl: z.url(),
    contentType: z.enum(SMOKE_CONTENT_TYPES),
    /** Both URLs share one expiry, which is never more than the 5 minutes of ARCHITECTURE.md § 10. */
    expiresAt: z.iso.datetime(),
  })
  .strict();

export type SmokeWriteRequest = z.infer<typeof smokeWriteRequestSchema>;
export type SmokeWriteResponse = z.infer<typeof smokeWriteResponseSchema>;
export type SmokeEnqueueRequest = z.infer<typeof smokeEnqueueRequestSchema>;
export type SmokeEnqueueResponse = z.infer<typeof smokeEnqueueResponseSchema>;
export type SmokeSignedUrlRequest = z.infer<typeof smokeSignedUrlRequestSchema>;
export type SmokeSignedUrlResponse = z.infer<typeof smokeSignedUrlResponseSchema>;

/**
 * Named in the contract's § System table but **not dispatchable**: a manual runbook procedure with
 * no trigger and no function in the packaging table. Recorded here so its absence from
 * `OPERATION_NAMES` is a documented derivation, not an omission.
 */
export const NON_DISPATCHABLE_PROCEDURES = Object.freeze(['engineering.runbook.deleteAccount'] as const);

/**
 * Removed at TA10 (D-5): replaced by the Firestore managed daily backup schedule, which is
 * configuration rather than an operation. `admin.system.status` reads the last successful backup.
 * Kept as a negative constant so a future re-addition has to be deliberate.
 */
export const REMOVED_OPERATIONS = Object.freeze(['system.backup.daily'] as const);

/**
 * Every dispatchable operation, in contract order, with the three T006 walking-skeleton operations
 * appended to the admin block they belong to.
 */
export const OPERATION_NAMES = Object.freeze([
  ...BOOTSTRAP_OPERATIONS,
  ...STUDENT_OPERATIONS,
  ...ADMIN_OPERATIONS,
  ...SMOKE_OPERATIONS,
  ...SYSTEM_OPERATIONS,
] as const);

export type OperationName = (typeof OPERATION_NAMES)[number];

export const operationNameSchema = z.enum(OPERATION_NAMES);

export function isOperationName(value: unknown): value is OperationName {
  return typeof value === 'string' && (OPERATION_NAMES as readonly string[]).includes(value);
}

/** Each § System operation is deployed as its own background function (packaging table, D-1). */
const SYSTEM_OPERATION_FUNCTIONS = Object.freeze({
  'system.ai.hourlyRun': 'hourlyAiRun',
  'system.ai.processQuestion': 'aiWorker',
  'system.ai.processSelectionEdit': 'aiWorker',
  'system.retention.purgeDaily': 'purgeDaily',
} as const satisfies Record<(typeof SYSTEM_OPERATIONS)[number], DispatchingFunctionName>);

/* ------------------------------------------------------------------ *
 * The registry
 * ------------------------------------------------------------------ */

/**
 * `placeholder` — the shape is not specified yet and the schema validates nothing (T005).
 * `specified`  — the shape is the frozen contract shape (T024 onwards).
 */
export type SchemaStatus = 'placeholder' | 'specified';

export interface OperationSpec<N extends OperationName = OperationName> {
  readonly name: N;
  readonly category: OperationCategory;
  readonly servedBy: DispatchingFunctionName;
  readonly request: z.ZodType;
  readonly response: z.ZodType;
  readonly schemaStatus: SchemaStatus;
}

export type OperationRegistry = { readonly [N in OperationName]: OperationSpec<N> };

/**
 * Builds the T024-frozen specs from the explicit schema table. A missing schema throws during module
 * construction, before a request can reach a dispatcher.
 */
function buildSpecs<N extends readonly OperationName[]>(
  names: N,
  category: OperationCategory,
  servedBy: (name: N[number]) => DispatchingFunctionName,
): { [K in N[number]]: OperationSpec<K> } {
  const out = {} as { [K in N[number]]: OperationSpec<K> };
  for (const name of names) {
    const schemas = OPERATION_SCHEMAS[name as keyof typeof OPERATION_SCHEMAS];
    if (schemas === undefined) {
      throw new Error(`No frozen request/response schema exists for operation "${name}".`);
    }
    const spec: OperationSpec = Object.freeze({
      name,
      category,
      servedBy: servedBy(name),
      request: schemas.request,
      response: schemas.response,
      schemaStatus: 'specified',
    });
    // One localized cast: the mapped return type cannot be built key-by-key without it.
    (out as Record<string, OperationSpec>)[name] = spec;
  }
  return out;
}

/**
 * The three T006 walking-skeleton specs remain written out because they are outside the reconciled
 * operations document; each still carries its original real request/response schema.
 */
const SMOKE_SPECS = {
  'admin.smoke.write': Object.freeze({
    name: 'admin.smoke.write',
    category: 'admin',
    servedBy: 'adminApi',
    request: smokeWriteRequestSchema,
    response: smokeWriteResponseSchema,
    schemaStatus: 'specified',
  }),
  'admin.smoke.enqueue': Object.freeze({
    name: 'admin.smoke.enqueue',
    category: 'admin',
    servedBy: 'adminApi',
    request: smokeEnqueueRequestSchema,
    response: smokeEnqueueResponseSchema,
    schemaStatus: 'specified',
  }),
  'admin.smoke.signedUrl': Object.freeze({
    name: 'admin.smoke.signedUrl',
    category: 'admin',
    servedBy: 'adminApi',
    request: smokeSignedUrlRequestSchema,
    response: smokeSignedUrlResponseSchema,
    schemaStatus: 'specified',
  }),
} as const satisfies { [N in (typeof SMOKE_OPERATIONS)[number]]: OperationSpec<N> };

/**
 * Complete by construction: `OperationRegistry` is a mapped type over `OperationName`, so a name
 * present in `OPERATION_NAMES` but missing here is a compile error. The tests prove the reverse
 * direction and the category/function partition.
 */
export const OPERATIONS: OperationRegistry = Object.freeze({
  ...buildSpecs(BOOTSTRAP_OPERATIONS, 'bootstrap', () => 'bootstrapApi'),
  ...buildSpecs(STUDENT_OPERATIONS, 'student', () => 'studentApi'),
  ...buildSpecs(ADMIN_OPERATIONS, 'admin', () => 'adminApi'),
  ...SMOKE_SPECS,
  ...buildSpecs(SYSTEM_OPERATIONS, 'system', (name) => SYSTEM_OPERATION_FUNCTIONS[name]),
});

/** Total over `OperationName`; the registry is exhaustive by type. */
export function operationSpec<N extends OperationName>(name: N): OperationSpec<N> {
  return OPERATIONS[name];
}

/** The operations one dispatching function is responsible for — the seam T006's registry needs. */
export function operationsServedBy(fn: DispatchingFunctionName): readonly OperationName[] {
  return OPERATION_NAMES.filter((name) => OPERATIONS[name].servedBy === fn);
}

/**
 * The T024 gate in executable form. Nothing calls it in Product code; tests call it so a future
 * accidental placeholder cannot masquerade as a frozen contract.
 */
export function assertSchemasFrozen(): void {
  const pending = OPERATION_NAMES.filter((name) => OPERATIONS[name].schemaStatus !== 'specified');
  if (pending.length > 0) {
    throw new Error(
      `The operation contract is not frozen: ${pending.length} operation(s) still carry a T005 ` +
        `placeholder schema — ${pending.join(', ')}`,
    );
  }
}

/* ------------------------------------------------------------------ *
 * The envelope
 * ------------------------------------------------------------------ */

/**
 * The wire shape, as a genuine discriminated union over `op` — `{ op, params }` where `params` is
 * the request schema of that exact operation. An unknown or absent `op` fails on the discriminator,
 * before any handler lookup runs.
 */
export type OperationEnvelope = {
  [N in OperationName]: { readonly op: N; readonly params: unknown };
}[OperationName];

function envelopeVariant(name: OperationName) {
  return z.object({ op: z.literal(name), params: OPERATIONS[name].request });
}

type EnvelopeVariant = ReturnType<typeof envelopeVariant>;

/**
 * `z.discriminatedUnion` requires a non-empty tuple. `OPERATION_NAMES` is a non-empty tuple literal,
 * but `Array.prototype.map` widens that to an array, so the head is re-established explicitly. The
 * throw is unreachable while the contract has any operation at all — and if that ever stops being
 * true, an empty envelope union must fail loudly rather than silently accept nothing.
 */
function buildEnvelopeVariants(): [EnvelopeVariant, ...EnvelopeVariant[]] {
  const [first, ...rest] = OPERATION_NAMES.map(envelopeVariant);
  if (first === undefined) {
    throw new Error('OPERATION_NAMES is empty: the operation envelope cannot be built.');
  }
  return [first, ...rest];
}

export const operationEnvelopeSchema = z.discriminatedUnion('op', buildEnvelopeVariants());
